import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Badge,
  Collapse,
  Chip,
  useTheme,
} from "@mui/material";
import { FilterList, Clear, Check, Close } from "@mui/icons-material";

// Importar componentes de filtros
import SearchFilter from "./SearchFilter";
import PriceRangeFilter from "./PriceRangeFilter";
import CategoryBrandFilter from "./CategoryBrandFilter";
import YearConditionFilter from "./YearConditionFilter";
import LocationFilter from "./LocationFilter";
import FeaturesFilter from "./FeaturesFilter";

const ProductFilters = ({
  filters,
  tempFilters,
  hasUnappliedChanges,
  updateFilters,
  updateTempFilters,
  applyFilters,
  resetTempFilters,
  clearAllFilters,
  activeFiltersCount,
  categories,
  brands,
  models,
  conditions,
  priceRanges,
  formatPrice,
  filterOptions,
  isMobile = false,
  loading = false,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(isMobile ? false : true);

  // Calcular altura del navbar basada en el theme
  const navbarHeight = theme.mixins.toolbar.minHeight || 64;
  const stickyTop = navbarHeight + 16;

  // Función para generar los chips de filtros aplicados
  const getAppliedFiltersChips = () => {
    const chips = [];

    // Función helper para eliminar un filtro y sincronizar filtros temporales
    const handleFilterDelete = (filterUpdate) => {
      // Actualizar tanto los filtros aplicados como los temporales
      updateTempFilters(filterUpdate);
    };

    // Filtro de búsqueda
    if (filters.search && filters.search.trim() !== "") {
      chips.push({
        label: `Búsqueda: "${filters.search}"`,
        key: "search",
        onDelete: () => handleFilterDelete({ search: "" }),
      });
    }

    // Filtro de categoría
    if (filters.category && filters.category !== "Todas") {
      chips.push({
        label: `Categoría: ${filters.category}`,
        key: "category",
        onDelete: () => handleFilterDelete({ category: "Todas" }),
      });
    }

    // Filtro de marca
    if (filters.brand && filters.brand !== "Todas") {
      chips.push({
        label: `Marca: ${filters.brand}`,
        key: "brand",
        onDelete: () => handleFilterDelete({ brand: "Todas" }),
      });
    }

    // Filtro de modelo
    if (filters.model && filters.model !== "Todas") {
      chips.push({
        label: `Modelo: ${filters.model}`,
        key: "model",
        onDelete: () => handleFilterDelete({ model: "Todas" }),
      });
    }

    // ✅ MEJORADO: Filtro de rango de precio - Solo mostrar si difiere de los valores por defecto del sistema
    if (
      filters.priceRange &&
      filterOptions?.priceRange &&
      (filters.priceRange[0] !== filterOptions.priceRange.min ||
        filters.priceRange[1] !== filterOptions.priceRange.max)
    ) {
      const minPrice = formatPrice
        ? formatPrice(filters.priceRange[0])
        : `$${filters.priceRange[0].toLocaleString()}`;
      const maxPrice = formatPrice
        ? formatPrice(filters.priceRange[1])
        : `$${filters.priceRange[1].toLocaleString()}`;
      chips.push({
        label: `Precio: ${minPrice} - ${maxPrice}`,
        key: "priceRange",
        onDelete: () =>
          handleFilterDelete({
            priceRange: [
              filterOptions.priceRange.min,
              filterOptions.priceRange.max,
            ],
          }),
      });
    }

    // ✅ MEJORADO: Filtro de año - Solo mostrar si difiere de los valores por defecto del sistema
    if (
      filters.yearRange &&
      filterOptions?.yearRange &&
      (filters.yearRange[0] !== filterOptions.yearRange.min ||
        filters.yearRange[1] !== filterOptions.yearRange.max)
    ) {
      chips.push({
        label: `Año: ${filters.yearRange[0]} - ${filters.yearRange[1]}`,
        key: "yearRange",
        onDelete: () =>
          handleFilterDelete({
            yearRange: [
              filterOptions.yearRange.min,
              filterOptions.yearRange.max,
            ],
          }),
      });
    }

    // Filtro de condición
    if (filters.condition && filters.condition !== "Todas") {
      chips.push({
        label: `Condición: ${filters.condition}`,
        key: "condition",
        onDelete: () => handleFilterDelete({ condition: "Todas" }),
      });
    }

    // Filtro de ubicación
    if (filters.ubicacion && filters.ubicacion !== "Todas") {
      chips.push({
        label: `Ubicación: ${filters.ubicacion}`,
        key: "ubicacion",
        onDelete: () => handleFilterDelete({ ubicacion: "Todas" }),
      });
    }

    // Filtro de características
    if (filters.features && filters.features.length > 0) {
      filters.features.forEach((feature, index) => {
        chips.push({
          label: `${feature}`,
          key: `feature_${index}`,
          onDelete: () => {
            const newFeatures = filters.features.filter((f) => f !== feature);
            handleFilterDelete({ features: newFeatures });
          },
        });
      });
    }

    return chips;
  };

  const appliedChips = getAppliedFiltersChips();

  useEffect(() => {
    if (hasUnappliedChanges) {
      applyFilters();
    }
  }, [tempFilters]);

  const FilterContent = () => (
    <Box sx={{ p: 2 }}>
      {/* Header fijo con botones de acción */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          // width: "100%",
          // alignItems: "stretch",
          mb: 2,
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 1,
          py: 1,
          mx: -2,
          px: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <FilterList color="primary" />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
          >
            {`Buscar aquí`}
            {activeFiltersCount > 0 && (
              <Badge badgeContent={activeFiltersCount} color="primary" />
            )}
          </Typography>
        </Box>

        {/* Botones de filtros en el header */}
        <Box sx={{ width: "100%", gap: 1 }}>
          {hasUnappliedChanges && (
            <>
              <Button
                variant="contained"
                startIcon={<Check />}
                onClick={applyFilters}
                disabled={loading}
                size="small"
                sx={{
                  bgcolor: "success.main",
                  "&:hover": { bgcolor: "success.dark" },
                  fontSize: "0.75rem",
                  px: 1.5,
                }}
              >
                {loading ? "Aplicando..." : "Aplicar"}
              </Button>

              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={resetTempFilters}
                size="small"
                sx={{
                  fontSize: "0.75rem",
                  px: 1,
                  minWidth: "auto",
                }}
              >
                Cancelar
              </Button>
            </>
          )}

          {/* Botón limpiar todos los filtros */}
          {activeFiltersCount > 0 && !hasUnappliedChanges && (
            <Button
              variant="contained"
              startIcon={<Clear />}
              onClick={clearAllFilters}
              color="error"
              sx={{
                width: "100%",
                fontSize: "0.875rem",
                fontWeight: 600,
                px: 2,
                py: 0.75,
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                  transform: "scale(1.02)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              Limpiar filtros
            </Button>
          )}
        </Box>
      </Box>

      {/* Sección de chips de filtros aplicados */}
      {appliedChips.length > 0 && (
        <Box
          sx={{
            mb: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1.5, fontWeight: 500 }}
          >
            Filtros aplicados:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              maxHeight: "120px",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(0,0,0,0.05)",
                borderRadius: "2px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.2)",
                borderRadius: "2px",
              },
            }}
          >
            {appliedChips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                onDelete={chip.onDelete}
                deleteIcon={<Close />}
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: "primary.50",
                  borderColor: "primary.200",
                  color: "primary.700",
                  fontSize: "0.75rem",
                  height: "28px",
                  "& .MuiChip-deleteIcon": {
                    fontSize: "16px",
                    color: "primary.500",
                    "&:hover": {
                      color: "primary.700",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "primary.100",
                    borderColor: "primary.300",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Contenido scrolleable - sin botones ya que están en el header */}
      <Box
        sx={{
          maxHeight: isMobile
            ? "calc(70vh - 80px)" // Restaurado ya que no hay botones
            : `calc(100vh - ${navbarHeight + 200}px)`, // Restaurado ya que no hay botones
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(0,0,0,0.1)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.3)",
            borderRadius: "3px",
            "&:hover": {
              background: "rgba(0,0,0,0.5)",
            },
          },
        }}
      >
        {/* Todos los filtros - SearchFilter aplica inmediatamente, otros usan filtros temporales */}
        <SearchFilter
          searchValue={tempFilters?.search || ""} // Usar filtro real para search
          onSearchChange={(value) => {
            // SearchFilter aplica inmediatamente
            updateTempFilters({ search: value });
          }}
          filterOptions={filterOptions} // ✅ NUEVO: Pasar opciones de filtros para autocompletado
        />

        <CategoryBrandFilter
          category={tempFilters?.category || "Todas"}
          brand={tempFilters?.brand || "Todas"} // ✅ NUEVO: Filtro de marca
          model={tempFilters?.model || "Todas"} // ✅ ACTUALIZADO: Filtro de modelo
          categories={categories || []}
          brands={brands || []} // ✅ NUEVO: Array de marcas
          models={models || []} // ✅ ACTUALIZADO: Array de modelos
          onCategoryChange={(value) => updateTempFilters({ category: value })}
          onBrandChange={(value) => updateTempFilters({ brand: value })} // ✅ NUEVO: Callback para marca
          onModelChange={(value) => updateTempFilters({ model: value })} // ✅ ACTUALIZADO: Callback para modelo
        />

        <PriceRangeFilter
          priceRange={tempFilters?.priceRange || [0, 500000000]}
          onPriceRangeChange={(value) =>
            updateTempFilters({ priceRange: value })
          }
          priceRanges={priceRanges || []}
          formatPrice={formatPrice}
        />

        <YearConditionFilter
          yearRange={tempFilters?.yearRange || [2015, 2025]}
          condition={tempFilters?.condition || "Todas"}
          conditions={conditions || []}
          filterOptions={filterOptions}
          onYearRangeChange={(value) => updateTempFilters({ yearRange: value })}
          onConditionChange={(value) => updateTempFilters({ condition: value })}
        />

        {/* <LocationFilter
          ubicacion={tempFilters?.ubicacion || "Todas"}
          ubicaciones={ubicaciones || []}
          onUbicacionChange={(value) => updateTempFilters({ ubicacion: value })}
        />

        <FeaturesFilter
          selectedFeatures={tempFilters?.features || []}
          allFeatures={allFeatures || []}
          onFeaturesChange={(value) => updateTempFilters({ features: value })}
        /> */}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Box sx={{ mb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setExpanded(!expanded)}
          sx={{ mb: expanded ? 2 : 0 }}
        >
          Filtros {activeFiltersCount > 0 && `(${activeFiltersCount})`}
        </Button>

        {/* Vista previa de chips cuando está colapsado en móvil */}
        {!expanded && appliedChips.length > 0 && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                maxHeight: "60px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "3px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(0,0,0,0.05)",
                  borderRadius: "2px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0,0,0,0.2)",
                  borderRadius: "2px",
                },
              }}
            >
              {appliedChips.slice(0, 6).map(
                (
                  chip // Mostrar máximo 6 chips
                ) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onDelete={chip.onDelete}
                    deleteIcon={<Close />}
                    variant="outlined"
                    size="small"
                    sx={{
                      backgroundColor: "primary.50",
                      borderColor: "primary.200",
                      color: "primary.700",
                      fontSize: "0.7rem",
                      height: "24px",
                      "& .MuiChip-deleteIcon": {
                        fontSize: "14px",
                        color: "primary.500",
                        "&:hover": {
                          color: "primary.700",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "primary.100",
                        borderColor: "primary.300",
                      },
                    }}
                  />
                )
              )}
              {appliedChips.length > 6 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    alignSelf: "center",
                    ml: 1,
                    fontSize: "0.7rem",
                  }}
                >
                  +{appliedChips.length - 6} más
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Collapse in={expanded}>
          <Paper elevation={1} sx={{ maxHeight: "70vh", overflow: "hidden" }}>
            <FilterContent />
          </Paper>
        </Collapse>
      </Box>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        // height: `calc(100vh - ${stickyTop + 40}px)`, // Dinámico
        position: "sticky",
        top: stickyTop,
        overflow: "hidden",
      }}
    >
      <FilterContent />
    </Paper>
  );
};

export default ProductFilters;
